import { useState, useMemo } from "react";
import { useStore, uid } from "../lib/store";
import { Card, Button, Input, Select, Label, Modal, Table, Th, Td, Empty, Textarea, Badge } from "../components/ui";
import { FinishedGoodCombobox } from "../components/FinishedGoodCombobox";
import { NewFinishedGoodModal } from "../components/NewFinishedGoodModal";
import type { Quotation, DocStatus, Party, QuotationRevision } from "../lib/types";
import { IconPlus, IconEdit, IconTrash, IconPrint, IconFile } from "../components/icons";
import { calcCostingTotals, calcDocTotals, fmtINR, nextNumber, printArea, professionalDocument, todayISO } from "../lib/utils";
import { userCan } from "../lib/permissions";

const STATUSES: DocStatus[] = ["Inquiry", "Quotation Sent", "Negotiation", "Order Confirmed", "Production", "Delivered"];
const GST_OPTIONS = [0, 5, 12, 18, 28];
const DEFAULT_QUOTATION_TERMS = `1. GST: Extra as applicable.
2. Payment Terms: 30% advance along with Purchase Order, 70% against inspection before dispatch.
3. Freight: Extra as actual.
4. Delivery: 30-45 days from the date of confirmed Purchase Order and advance payment.
5. Validity: 10 days from the date of offer.`;
const DEFAULT_ITEM_DESCRIPTION = "11/0.433 kV, Copper Wound, Outdoor Type, Oil Immersed, ONAN Cooling, BIS Compliant, Complete with Standard Accessories.";

function getDefaultQuotationTerms(settings: any) {
  const format = (settings.documentFormats || []).find((f: any) => f.active && f.documentType === "Quotation");
  const terms = (format?.terms || []).filter((t: any) => t.active).sort((a: any, b: any) => a.order - b.order);
  return terms.length ? terms.map((t: any, i: number) => `${i + 1}. ${t.text}`).join("\n") : DEFAULT_QUOTATION_TERMS;
}

export function Quotations() {
  const { db, setDB, currentUser, log } = useStore();
  const isAdmin = currentUser?.role === "admin";
  const canCreate = userCan(currentUser, "quotations", "create");
  const canEdit = userCan(currentUser, "quotations", "edit");
  const canDelete = userCan(currentUser, "quotations", "delete");
  const canPrint = userCan(currentUser, "quotations", "print");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState<Quotation | null>(null);
  const [newItemForRow, setNewItemForRow] = useState<{ idx: number; seed: string } | null>(null);

  // Inline "Create New Customer" modal (from Customer dropdown in Quotation form)
  const blankCustomer = (): Party => ({
    id: "", name: "", type: "customer", gst: "", address: "", city: "",
    contactPerson: "", mobile: "", email: "", paymentTerms: "30 days", creditLimit: 0,
    ownerId: currentUser!.id, createdAt: new Date().toISOString(),
  });
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState<Party>(blankCustomer());
  const openNewCustomer = () => { setNewCustomer(blankCustomer()); setShowNewCustomer(true); };
  const saveNewCustomer = () => {
    const name = (newCustomer.name || "").trim();
    if (!name) return alert("Customer name is required");
    const created: Party = { ...newCustomer, name, id: uid() };
    setDB(d => ({ ...d, parties: [created, ...d.parties] }));
    setForm(f => ({ ...f, customerId: created.id }));
    log(`Created party: ${created.name} (from Quotation)`, "Party Master");
    setShowNewCustomer(false);
  };

  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<"all" | "open" | "revised" | "confirmed" | "closed">("all");
  const quotationState = (q: Quotation): "open" | "revised" | "confirmed" | "closed" => {
    if (q.closeReason) return "closed";
    if (q.status === "Order Confirmed" || q.status === "Production" || q.status === "Delivered") return "confirmed";
    if ((q.revisions || []).length > 0) return "revised";
    return "open";
  };

  const list = useMemo(() => {
    let arr = isAdmin ? db.quotations : db.quotations.filter(q => q.ownerId === currentUser?.id);
    if (ownerFilter !== "all") arr = arr.filter(q => q.ownerId === ownerFilter);
    if (stateFilter !== "all") arr = arr.filter(q => quotationState(q) === stateFilter);
    return arr.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db.quotations, isAdmin, currentUser, ownerFilter, stateFilter]);

  const customers = db.parties.filter(p => p.type === "customer" && (isAdmin || p.ownerId === currentUser?.id));
  const myCostings = db.costings.filter(c => isAdmin || c.ownerId === currentUser?.id);

  const companies = db.settings.companies || [];
  const companyOf = (q: Quotation) => companies.find(c => c.id === q.companyId);
  const qMoney = (v: number, q: Quotation) => {
    if (q.currency === "USD") {
      const usd = (q.exchangeRate || 0) > 0 ? v / q.exchangeRate! : v;
      return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return fmtINR(v);
  };
  const companyNumber = (companyId: string) => nextNumber("QTN", db.quotations.filter(q => (q.companyId || "") === companyId));

  const blank = (): Quotation => ({
    id: "", number: companyNumber(""), date: todayISO(), customerId: customers[0]?.id || "", costingId: "",
    companyId: "", currency: "INR", exchangeRate: 0,
    items: [{ name: "100 kVA Distribution Transformer", description: DEFAULT_ITEM_DESCRIPTION, qty: 1, rate: 185000, gst: 18 }],
    terms: getDefaultQuotationTerms(db.settings),
    status: "Quotation Sent", ownerId: currentUser!.id, createdAt: new Date().toISOString(),
  });
  const [form, setForm] = useState<Quotation>(blank());

  const openNew = (presetCostingId?: string) => {
    const f = blank();
    if (presetCostingId) {
      const c = db.costings.find(x => x.id === presetCostingId);
      if (c) {
        const t = calcCostingTotals(c.materials, c.gstRate, c.marginPct);
        f.costingId = c.id;
        f.customerId = c.customerId || f.customerId;
        f.items = [{ name: `${c.kva || ""} KVA Transformer — ${c.title}`, qty: 1, rate: Math.round(t.subTotal), gst: c.gstRate }];
      }
    }
    setEdit(null); setForm(f); setOpen(true);
  };
  const openEdit = (q: Quotation) => { setEdit(q); setForm({...q, items: q.items.map(i => ({...i}))}); setOpen(true); };

  const save = () => {
    if (!form.customerId) return alert("Select a customer");
    if (edit) {
      setDB(d => ({ ...d, quotations: d.quotations.map(q => q.id === edit.id ? form : q) }));
      log(`Updated quotation ${form.number}`, "Quotations");
    } else {
      setDB(d => ({ ...d, quotations: [{...form, id: uid()}, ...d.quotations] }));
      log(`Created quotation ${form.number}`, "Quotations");
    }
    setOpen(false);
  };
  const remove = (q: Quotation) => {
    if (!confirm(`Delete ${q.number}?`)) return;
    setDB(d => ({ ...d, quotations: d.quotations.filter(x => x.id !== q.id) }));
    log(`Deleted quotation ${q.number}`, "Quotations");
  };

  const updateItem = (i: number, key: string, val: any) => {
    setForm(f => ({...f, items: f.items.map((it, idx) => idx === i ? {...it, [key]: key === "name" || key === "description" ? val : Number(val)} : it)}));
  };
  const pickFinishedGood = (i: number, fg: { name: string; saleRate: number; gstRate: number }) => {
    // Look up the LATEST Costing Sheet for this product name (approved > any) — sale price = pre-GST subTotal.
    // The Costing Sheet is only read; editing the quotation rate never mutates the sheet.
    const norm = (s: string) => (s || "").trim().toLowerCase();
    const matches = db.costings.filter(c => norm(c.productName || "") === norm(fg.name));
    const preferred = matches.filter(c => c.status === "approved");
    const pool = preferred.length > 0 ? preferred : matches;
    const latest = pool.slice().sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0];
    let costingRate = 0;
    if (latest) {
      const t = calcCostingTotals(latest.materials, latest.gstRate, latest.marginPct);
      costingRate = Math.round(t.subTotal);
    }
    setForm(f => ({
      ...f,
      items: f.items.map((it, idx) => idx === i ? {
        ...it,
        name: fg.name,
        rate: costingRate || fg.saleRate || it.rate || 0,
        gst: latest?.gstRate ?? (fg.gstRate ?? it.gst),
      } : it),
    }));
    if (latest) log(`Quotation: auto-filled sale price ${fmtINR(costingRate)} from ${latest.number} for ${fg.name}`, "Quotations");
  };
  const addItem = () => setForm(f => ({...f, items: [...f.items, { name: "", description: "", qty: 1, rate: 0, gst: 18 }]}));
  const delItem = (i: number) => setForm(f => ({...f, items: f.items.filter((_, idx) => idx !== i)}));
  const totals = calcDocTotals(form.items);

  const saveAsDefaultTerms = () => {
    const lines = form.terms.split("\n").map(x => x.replace(/^\s*\d+[.)-]?\s*/, "").trim()).filter(Boolean);
    if (!lines.length) return alert("Enter at least one term to save as default.");
    const now = new Date().toISOString();
    setDB(d => {
      const existing = d.settings.documentFormats || [];
      const quotationFormat = existing.find((f: any) => f.documentType === "Quotation");
      const updatedFormat = {
        ...(quotationFormat || {
          id: uid(), documentType: "Quotation", formatName: "Quotation Standard Format", active: true,
          companyName: d.settings.name, address: d.settings.address, gstNo: d.settings.gst,
          contactDetails: `${d.settings.email} | ${d.settings.phone}`, headerContent: "", footerContent: "",
          bankDetails: "", declaration: "", signatureName: "Authorized Signatory", qrCode: true,
          pageSize: "A4", orientation: "Portrait", createdAt: now,
        }),
        active: true,
        terms: lines.map((text, index) => ({ id: `quotation-term-${index + 1}`, text, active: true, order: index + 1 })),
        updatedAt: now,
      };
      return {
        ...d,
        settings: {
          ...d.settings,
          documentFormats: quotationFormat
            ? existing.map((f: any) => f.id === quotationFormat.id ? updatedFormat : f)
            : [updatedFormat as any, ...existing],
        },
      };
    });
    log("Updated default quotation terms", "Quotations");
    alert("Default quotation terms saved.");
  };

  const convert = (q: Quotation, to: "proforma" | "salesorder") => {
    if (to === "proforma") {
      const np = {
        id: uid(), number: nextNumber("PI", db.proformas), date: todayISO(),
        quotationId: q.id, customerId: q.customerId, items: q.items.map(i => ({...i})),
        paymentTerms: "50% advance, 50% before dispatch", transport: "By Road",
        ownerId: currentUser!.id, createdAt: new Date().toISOString(),
      };
      setDB(d => ({ ...d, proformas: [np, ...d.proformas], quotations: d.quotations.map(x => x.id === q.id ? {...x, status: "Negotiation"} : x) }));
      log(`Converted ${q.number} → Proforma ${np.number}`, "Quotations");
      alert(`Proforma Invoice ${np.number} created.`);
    } else {
      const so = {
        id: uid(), number: nextNumber("SO", db.salesOrders), date: todayISO(),
        customerId: q.customerId, items: q.items.map(i => ({...i})),
        deliveryDate: "", status: "Confirmed" as const,
        ownerId: currentUser!.id, createdAt: new Date().toISOString(),
      };
      setDB(d => ({ ...d, salesOrders: [so, ...d.salesOrders], quotations: d.quotations.map(x => x.id === q.id ? {...x, status: "Order Confirmed"} : x) }));
      log(`Converted ${q.number} → SO ${so.number}`, "Quotations");
      alert(`Sales Order ${so.number} created.`);
    }
  };

  const [previewQ, setPreviewQ] = useState<Quotation | null>(null);

  // Revise / Close / History modals
  type ReviseItem = { name: string; description?: string; qty: number; rate: number; gst: number };
  const [reviseQ, setReviseQ] = useState<Quotation | null>(null);
  const [reviseItems, setReviseItems] = useState<ReviseItem[]>([]);
  const [reviseDiscountPct, setReviseDiscountPct] = useState<number>(0);
  const [reviseNote, setReviseNote] = useState<string>("");
  const [closeQ, setCloseQ] = useState<Quotation | null>(null);
  const [closeReason, setCloseReason] = useState<string>("Price High");
  const [closeNote, setCloseNote] = useState<string>("");
  const [historyQ, setHistoryQ] = useState<Quotation | null>(null);
  const CLOSE_REASONS = ["Price High", "Customer Cancelled", "Competitor", "No Response", "Other"];

  const stateBadgeColor: Record<string, "slate" | "blue" | "amber" | "emerald" | "rose"> = {
    open: "blue", revised: "amber", confirmed: "emerald", closed: "rose",
  };
  const stateLabel: Record<string, string> = { open: "Open", revised: "Revised", confirmed: "Confirmed", closed: "Closed" };

  const openRevise = (q: Quotation) => {
    setReviseQ(q);
    setReviseItems(q.items.map(i => ({ ...i })));
    setReviseDiscountPct(0);
    setReviseNote("");
  };
  const applyPctToAllRates = () => {
    const pct = Number(reviseDiscountPct) || 0;
    if (pct <= 0) return;
    setReviseItems(items => items.map(it => ({ ...it, rate: Math.round(it.rate * (1 - pct / 100)) })));
  };
  const saveRevision = () => {
    if (!reviseQ) return;
    const items = reviseItems;
    const t = calcDocTotals(items);
    const nextRev = ((reviseQ.revisions || []).slice(-1)[0]?.revNo || 0) + 1;
    const rev: QuotationRevision = {
      revNo: nextRev,
      date: todayISO(),
      revisedById: currentUser!.id,
      revisedByName: currentUser!.name || currentUser!.email || "User",
      items: items.map(i => ({ ...i })),
      subTotal: t.sub,
      gst: t.gst,
      total: t.total,
      discountPct: Number(reviseDiscountPct) || undefined,
      note: reviseNote || undefined,
    };
    setDB(d => ({
      ...d,
      quotations: d.quotations.map(x => x.id === reviseQ.id
        ? { ...x, items: rev.items, revisions: [...(x.revisions || []), rev] }
        : x),
    }));
    log(`Revised ${reviseQ.number} → R${nextRev} (total ${fmtINR(t.total)})`, "Quotations");
    setReviseQ(null);
  };

  const openClose = (q: Quotation) => {
    setCloseQ(q);
    setCloseReason("Price High");
    setCloseNote("");
  };
  const saveClose = () => {
    if (!closeQ) return;
    if (!closeReason) return alert("Reason is required.");
    setDB(d => ({
      ...d,
      quotations: d.quotations.map(x => x.id === closeQ.id
        ? { ...x, closeReason, closeNote: closeNote || undefined, closedAt: new Date().toISOString(), closedById: currentUser!.id }
        : x),
    }));
    log(`Closed ${closeQ.number}: ${closeReason}${closeNote ? " — " + closeNote : ""}`, "Quotations");
    setCloseQ(null);
  };
  const reopenQ = (q: Quotation) => {
    if (!confirm(`Reopen ${q.number}?`)) return;
    setDB(d => ({
      ...d,
      quotations: d.quotations.map(x => x.id === q.id ? { ...x, closeReason: undefined, closeNote: undefined, closedAt: undefined, closedById: undefined } : x),
    }));
    log(`Reopened ${q.number}`, "Quotations");
  };

  const openPreview = (q: Quotation) => setPreviewQ(q);

  const buildQuotationHTML = (q: Quotation) => {
    const cust = db.parties.find(p => p.id === q.customerId);
    const comp = companyOf(q);
    const effSettings = comp ? {
      ...db.settings,
      name: comp.name,
      address: comp.address || db.settings.address,
      gst: comp.gst || "",
      email: comp.email || "",
      phone: comp.phone || "",
      logoUrl: comp.logo || "",
      logoText: comp.name.split(/\s+/).map(w => w[0]).join("").slice(0, 3).toUpperCase(),
    } : db.settings;
    const t = calcDocTotals(q.items);
    const usdNote = q.currency === "USD" ? `<div class="box" style="background:#eff6ff"><b>All amounts in USD ($)</b>${(q.exchangeRate || 0) > 0 ? ` · Converted at 1 USD = ₹${q.exchangeRate}` : ""}</div>` : "";
    const body = `
      <div class="box"><div class="section-title">Bill To</div><b>${cust?.name || ""}</b><br/>${cust?.address || ""}<br/>GST: ${cust?.gst || ""}<br/>Contact: ${cust?.mobile || ""} | ${cust?.email || ""}</div>
      ${usdNote}
      <table><thead><tr><th>#</th><th>Description</th><th class="right">Qty</th><th class="right">Rate</th><th class="right">GST%</th><th class="right">Amount</th></tr></thead>
      <tbody>${q.items.map((i, idx) => `<tr><td>${idx+1}</td><td><b>${i.name}</b>${i.description ? `<br/><span class="muted">${i.description}</span>` : ""}</td><td class="right">${i.qty}</td><td class="right">${qMoney(i.rate, q)}</td><td class="right">${i.gst}</td><td class="right">${qMoney(i.qty*i.rate, q)}</td></tr>`).join("")}</tbody></table>
      <div class="totals">
        <div><span>Sub Total</span><b>${qMoney(t.sub, q)}</b></div>
        <div><span>${q.currency === "USD" ? "Tax (GST)" : "GST"}</span><b>${qMoney(t.gst, q)}</b></div>
        <div class="grand"><span>Grand Total</span><b>${qMoney(t.total, q)}</b></div>
      </div>
      ${comp?.bankDetails ? `<div class="box"><div class="section-title">Bank Details</div>${comp.bankDetails.replace(/\n/g, "<br/>")}</div>` : ""}
      <div class="box"><div class="section-title">Terms & Conditions</div><pre style="white-space:pre-wrap;font-family:inherit;font-size:12px;margin:6px 0">${q.terms}</pre></div>
    `;
    return professionalDocument(effSettings, { title: "Quotation", number: q.number, date: q.date, body, accent: "#2563eb", skipFormatTerms: true, forceSettingsHeader: !!comp });
  };

  const printQ = (q: Quotation) => {
    printArea(buildQuotationHTML(q), q.number);
  };

  const printFromPreview = () => {
    const frame = document.querySelector('iframe[data-testid="quot-preview-iframe"]') as HTMLIFrameElement | null;
    if (frame && frame.contentWindow) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold">Quotations</h1><p className="text-sm text-slate-500">Create, convert and track quotations</p></div>
        <div className="flex gap-2 flex-wrap items-end">
          {isAdmin && (
            <div>
              <Label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">Owner</Label>
              <Select value={ownerFilter} onChange={(e: any) => setOwnerFilter(e.target.value)} className="w-44" data-testid="quot-owner-filter">
                <option value="all">All Owners</option>
                {db.users.filter(u => u.role === "sales").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </Select>
            </div>
          )}
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">State</Label>
            <Select value={stateFilter} onChange={(e: any) => setStateFilter(e.target.value)} className="w-36" data-testid="quot-state-filter">
              <option value="all">All States</option>
              <option value="open">Open</option>
              <option value="revised">Revised</option>
              <option value="confirmed">Confirmed</option>
              <option value="closed">Closed</option>
            </Select>
          </div>
          {canCreate && myCostings.length > 0 && (
            <Select className="w-56" onChange={(e: any) => e.target.value && openNew(e.target.value)}>
              <option value="">From costing sheet…</option>
              {myCostings.filter(c => c.status === "approved").map(c => <option key={c.id} value={c.id}>{c.number} — {c.title}</option>)}
            </Select>
          )}
          {canCreate && <Button onClick={() => openNew()}><IconPlus size={14}/> New Quotation</Button>}
        </div>
      </div>

      <Card>
        <Table>
          <thead><tr><Th>#</Th><Th>Date</Th><Th>Customer</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th>State</Th><Th>Owner</Th><Th></Th></tr></thead>
          <tbody>
            {list.map(q => {
              const t = calcDocTotals(q.items);
              const st = quotationState(q);
              const revCount = (q.revisions || []).length;
              return (
                <tr key={q.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <Td className="font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      <button
                        className="text-indigo-600 hover:underline"
                        onClick={() => openPreview(q)}
                        title="Click to preview the printable A4 quotation"
                        data-testid={`quot-view-${q.id}`}
                      >{q.number}</button>
                      {companyOf(q) && <span className="text-[9px] font-semibold text-indigo-700 bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-300 px-1.5 py-0.5 rounded" title={`Selling company: ${companyOf(q)!.name}`} data-testid={`quot-company-${q.id}`}>{companyOf(q)!.name.slice(0, 12)}</span>}
                      {q.currency === "USD" && <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 px-1.5 py-0.5 rounded">USD</span>}
                      {revCount > 0 && (
                        <button
                          onClick={() => setHistoryQ(q)}
                          className="text-[9px] font-semibold text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded hover:bg-amber-200"
                          title="View revision history"
                          data-testid={`quot-history-${q.id}`}
                        >R{revCount}</button>
                      )}
                    </div>
                  </Td>
                  <Td>{q.date}</Td>
                  <Td>{db.parties.find(p => p.id === q.customerId)?.name}</Td>
                  <Td>{q.items.length}</Td>
                  <Td className="font-semibold">{fmtINR(t.total)}</Td>
                  <Td>
                    <Select disabled={!canEdit || st === "closed"} value={q.status} onChange={(e: any) => {
                      setDB(d => ({ ...d, quotations: d.quotations.map(x => x.id === q.id ? {...x, status: e.target.value} : x) }));
                      log(`Status of ${q.number} → ${e.target.value}`, "Quotations");
                    }} className="text-xs py-1">
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </Select>
                  </Td>
                  <Td>
                    <Badge color={stateBadgeColor[st]} className={st === "closed" ? "cursor-help" : ""}>
                      <span title={st === "closed" ? `${q.closeReason || ""}${q.closeNote ? " — " + q.closeNote : ""}` : ""}>{stateLabel[st]}</span>
                    </Badge>
                  </Td>
                  <Td className="text-xs">{db.users.find(u => u.id === q.ownerId)?.name}</Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap">
                      {canEdit && st !== "closed" && <Button size="sm" variant="ghost" onClick={() => openEdit(q)} title="Edit"><IconEdit size={14}/></Button>}
                      {canEdit && st !== "closed" && <Button size="sm" variant="outline" onClick={() => openRevise(q)} title="Revise quotation" data-testid={`quot-revise-${q.id}`}>Revise</Button>}
                      {canPrint && <Button size="sm" variant="ghost" onClick={() => printQ(q)} title="Download PDF"><IconPrint size={14}/></Button>}
                      {userCan(currentUser, "proformas", "create") && st !== "closed" && <Button size="sm" variant="outline" onClick={() => convert(q, "proforma")} title="Convert to Proforma"><IconFile size={12}/> PI</Button>}
                      {userCan(currentUser, "salesorders", "create") && st !== "closed" && <Button size="sm" variant="outline" onClick={() => convert(q, "salesorder")} title="Convert to Sales Order">SO</Button>}
                      {canEdit && st !== "closed" && st !== "confirmed" && <Button size="sm" variant="outline" onClick={() => openClose(q)} title="Close (not confirmed)" data-testid={`quot-close-${q.id}`}>Close</Button>}
                      {canEdit && st === "closed" && <Button size="sm" variant="ghost" onClick={() => reopenQ(q)} title="Reopen" data-testid={`quot-reopen-${q.id}`}>Reopen</Button>}
                      {canDelete && <Button size="sm" variant="ghost" onClick={() => remove(q)} title="Delete"><IconTrash size={14}/></Button>}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {list.length === 0 && <Empty title="No quotations yet"/>}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.number}` : "New Quotation"} size="xl">
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Quotation No.</Label><Input value={form.number} disabled/></div>
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e: any) => setForm({...form, date: e.target.value})}/></div>
          <div><Label>Customer *</Label>
            <Select
              value={form.customerId}
              onChange={(e: any) => {
                const v = e.target.value;
                if (v === "__new__") { openNewCustomer(); return; }
                setForm({ ...form, customerId: v });
              }}
              data-testid="qtn-customer-select"
            >
              <option value="__new__" style={{ background: "#eef2ff", color: "#4338ca", fontWeight: 700 }}>➕ Create New Customer…</option>
              <option value="">— Select —</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-3">
          <div>
            <Label>Selling Company</Label>
            <Select value={form.companyId || ""} onChange={(e: any) => {
              const v = e.target.value;
              setForm(f => ({ ...f, companyId: v, ...(edit ? {} : { number: companyNumber(v) }) }));
            }} data-testid="qtn-company-select">
              <option value="">— Main Company ({db.settings.name}) —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </div>
          <div>
            <Label>Currency</Label>
            <Select value={form.currency || "INR"} onChange={(e: any) => setForm({ ...form, currency: e.target.value })} data-testid="qtn-currency">
              <option value="INR">INR (₹)</option>
              <option value="USD">USD ($)</option>
            </Select>
          </div>
          {form.currency === "USD" && (
            <div>
              <Label>Exchange Rate (1 USD = ₹)</Label>
              <Input type="number" value={form.exchangeRate || ""} placeholder="e.g. 83.5" onChange={(e: any) => setForm({ ...form, exchangeRate: Number(e.target.value) || 0 })} data-testid="qtn-exchange-rate" />
              <div className="text-[10px] text-slate-500 mt-1">Optional — converts INR rates to USD; leave blank if rates are already USD</div>
            </div>
          )}
        </div>

        <div className="mt-3 border border-slate-200 dark:border-slate-700 rounded-lg overflow-visible">
          <Table>
            <thead><tr><Th>Item Name / Description</Th><Th>Qty</Th><Th>Rate</Th><Th>GST%</Th><Th>Amount</Th><Th></Th></tr></thead>
            <tbody>
              {form.items.map((it, i) => {
                const finishedGoods = db.items.filter(x => x.category === "Finished Goods");
                return (
                <tr key={i}>
                  <Td className="min-w-[280px]">
                    <Label>Item Name</Label>
                    <FinishedGoodCombobox
                      items={finishedGoods}
                      value={it.name}
                      onPick={(fg) => pickFinishedGood(i, fg)}
                      onCreateNew={(seed) => setNewItemForRow({ idx: i, seed })}
                      testId={`quot-item-combo-${i}`}
                    />
                    <Label className="mt-2">Description</Label>
                    <Textarea rows={2} value={it.description || ""} placeholder="11/0.433 kV, Copper Wound, Outdoor Type, Oil Immersed, ONAN Cooling, BIS Compliant, Complete with Standard Accessories." onChange={(e: any) => updateItem(i, "description", e.target.value)}/>
                  </Td>
                  <Td><Input type="number" value={it.qty} onChange={(e: any) => updateItem(i, "qty", e.target.value)}/></Td>
                  <Td><Input type="number" value={it.rate} onChange={(e: any) => updateItem(i, "rate", e.target.value)}/></Td>
                  <Td><Select value={it.gst} onChange={(e: any) => updateItem(i, "gst", e.target.value)}>{GST_OPTIONS.map(rate => <option key={rate} value={rate}>{rate}%</option>)}</Select></Td>
                  <Td className="font-medium">{fmtINR(it.qty * it.rate)}</Td>
                  <Td><Button size="sm" variant="ghost" onClick={() => delItem(i)}><IconTrash size={14}/></Button></Td>
                </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        <div className="mt-2"><Button size="sm" variant="outline" onClick={addItem}><IconPlus size={14}/> Add Item</Button></div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="mb-0">Editable Default Terms & Conditions</Label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({...form, terms: getDefaultQuotationTerms(db.settings)})}>Load Default Terms</Button>
                {currentUser?.role === "admin" && <Button type="button" size="sm" variant="outline" onClick={saveAsDefaultTerms}>Save as Default</Button>}
              </div>
            </div>
            <Textarea key={form.number} rows={8} value={form.terms} onChange={(e: any) => setForm({...form, terms: e.target.value})}/>
            <p className="mt-1 text-xs text-slate-500">These terms print on the quotation PDF and can be changed for this quotation. Admin can save them as the default quotation terms.</p>
          </div>
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/40 text-sm space-y-1 self-start">
            <div className="flex justify-between"><span>Sub Total</span><b>{fmtINR(totals.sub)}</b></div>
            <div className="flex justify-between"><span>GST</span><b>{fmtINR(totals.gst)}</b></div>
            <div className="flex justify-between text-base border-t pt-1 mt-1"><span>Grand Total</span><b className="text-emerald-600">{fmtINR(totals.total)}</b></div>
            {form.currency === "USD" && (form.exchangeRate || 0) > 0 && (
              <div className="text-[11px] text-slate-500 border-t pt-1" data-testid="qtn-usd-note">PDF shows USD: Grand Total ≈ <b>{qMoney(totals.total, form)}</b> @ 1 USD = ₹{form.exchangeRate}</div>
            )}
            {form.currency === "USD" && !(form.exchangeRate || 0) && (
              <div className="text-[11px] text-slate-500 border-t pt-1" data-testid="qtn-usd-note">No exchange rate set — entered rates are treated as USD on the PDF</div>
            )}
            <div className="mt-2"><Label>Status</Label>
              <Select value={form.status} onChange={(e: any) => setForm({...form, status: e.target.value})}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save}>{edit ? "Update" : "Create"}</Button>
        </div>
      </Modal>

      <NewFinishedGoodModal
        open={!!newItemForRow}
        seedName={newItemForRow?.seed || ""}
        onClose={() => setNewItemForRow(null)}
        onCreated={(item) => {
          if (newItemForRow) pickFinishedGood(newItemForRow.idx, item as any);
          setNewItemForRow(null);
        }}
        testIdPrefix="quot-new-fg"
      />

      {/* Inline "Create New Customer" modal — persists into Party Master and auto-selects */}
      <Modal open={showNewCustomer} onClose={() => setShowNewCustomer(false)} title="Create New Customer" size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="qtn-new-customer-modal">
          <div className="sm:col-span-2">
            <Label>Customer Name *</Label>
            <Input value={newCustomer.name} onChange={(e: any) => setNewCustomer({ ...newCustomer, name: e.target.value })} autoFocus data-testid="qtn-new-customer-name" />
          </div>
          <div><Label>GSTIN</Label><Input value={newCustomer.gst} onChange={(e: any) => setNewCustomer({ ...newCustomer, gst: e.target.value })} data-testid="qtn-new-customer-gst" /></div>
          <div><Label>Contact Person</Label><Input value={newCustomer.contactPerson} onChange={(e: any) => setNewCustomer({ ...newCustomer, contactPerson: e.target.value })} data-testid="qtn-new-customer-contact" /></div>
          <div><Label>Mobile</Label><Input value={newCustomer.mobile} onChange={(e: any) => setNewCustomer({ ...newCustomer, mobile: e.target.value })} data-testid="qtn-new-customer-mobile" /></div>
          <div><Label>Email</Label><Input type="email" value={newCustomer.email} onChange={(e: any) => setNewCustomer({ ...newCustomer, email: e.target.value })} data-testid="qtn-new-customer-email" /></div>
          <div><Label>City</Label><Input value={newCustomer.city} onChange={(e: any) => setNewCustomer({ ...newCustomer, city: e.target.value })} data-testid="qtn-new-customer-city" /></div>
          <div><Label>Payment Terms</Label><Input value={newCustomer.paymentTerms} onChange={(e: any) => setNewCustomer({ ...newCustomer, paymentTerms: e.target.value })} data-testid="qtn-new-customer-terms" /></div>
          <div className="sm:col-span-2">
            <Label>Address</Label>
            <Textarea value={newCustomer.address} onChange={(e: any) => setNewCustomer({ ...newCustomer, address: e.target.value })} rows={2} data-testid="qtn-new-customer-address" />
          </div>
          <div><Label>Credit Limit</Label><Input type="number" value={newCustomer.creditLimit} onChange={(e: any) => setNewCustomer({ ...newCustomer, creditLimit: Number(e.target.value) || 0 })} data-testid="qtn-new-customer-credit" /></div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setShowNewCustomer(false)} data-testid="qtn-new-customer-cancel">Cancel</Button>
          <Button onClick={saveNewCustomer} data-testid="qtn-new-customer-save">Create &amp; Select</Button>
        </div>
      </Modal>

      {/* A4 Printable Preview modal — clickable quotation number */}
      <Modal
        open={!!previewQ}
        onClose={() => setPreviewQ(null)}
        title={previewQ ? `Quotation Preview — ${previewQ.number}` : ""}
        size="xl"
      >
        {previewQ && (
          <div className="space-y-3" data-testid="quot-preview-modal">
            <div className="flex items-center justify-end gap-2">
              {canEdit && (
                <Button variant="outline" onClick={() => { const q = previewQ; setPreviewQ(null); openEdit(q); }} data-testid="quot-preview-edit">
                  <IconEdit size={14}/> Edit
                </Button>
              )}
              <Button variant="outline" onClick={printFromPreview} data-testid="quot-preview-print">
                <IconPrint size={14}/> Print
              </Button>
              {canPrint && (
                <Button onClick={() => printQ(previewQ)} data-testid="quot-preview-pdf">
                  <IconFile size={14}/> Download PDF
                </Button>
              )}
              <Button variant="ghost" onClick={() => setPreviewQ(null)} data-testid="quot-preview-close">Close</Button>
            </div>
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 flex justify-center overflow-auto max-h-[75vh]">
              <iframe
                srcDoc={buildQuotationHTML(previewQ)}
                title={`Quotation ${previewQ.number}`}
                data-testid="quot-preview-iframe"
                style={{ width: "820px", height: "1123px", border: 0, background: "#fff", boxShadow: "0 6px 20px rgba(15,23,42,0.15)" }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Revise Quotation Modal */}
      <Modal open={!!reviseQ} onClose={() => setReviseQ(null)} title={reviseQ ? `Revise ${reviseQ.number} — R${((reviseQ.revisions || []).slice(-1)[0]?.revNo || 0) + 1}` : ""} size="xl">
        {reviseQ && (() => {
          const t = calcDocTotals(reviseItems);
          const prevTotal = calcDocTotals(reviseQ.items).total;
          return (
            <div className="space-y-3" data-testid="quot-revise-modal">
              <div className="text-xs text-slate-500">Edit rates or apply an overall discount %. Saving creates a new revision entry and updates the current quotation with these values. Previous total: <b>{fmtINR(prevTotal)}</b></div>
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-x-auto">
                <Table>
                  <thead><tr><Th>Item</Th><Th className="text-right">Qty</Th><Th className="text-right">Rate</Th><Th className="text-right">GST%</Th><Th className="text-right">Amount</Th></tr></thead>
                  <tbody>
                    {reviseItems.map((it, i) => (
                      <tr key={i}>
                        <Td className="min-w-[280px]"><Input value={it.name} onChange={(e: any) => setReviseItems(items => items.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} /></Td>
                        <Td><Input type="number" value={it.qty} onChange={(e: any) => setReviseItems(items => items.map((x, idx) => idx === i ? { ...x, qty: Number(e.target.value) || 0 } : x))} className="w-20 text-right" data-testid={`quot-rev-qty-${i}`} /></Td>
                        <Td><Input type="number" value={it.rate} onChange={(e: any) => setReviseItems(items => items.map((x, idx) => idx === i ? { ...x, rate: Number(e.target.value) || 0 } : x))} className="w-32 text-right" data-testid={`quot-rev-rate-${i}`} /></Td>
                        <Td>
                          <Select value={it.gst} onChange={(e: any) => setReviseItems(items => items.map((x, idx) => idx === i ? { ...x, gst: Number(e.target.value) } : x))} className="w-20">
                            {GST_OPTIONS.map(g => <option key={g} value={g}>{g}%</option>)}
                          </Select>
                        </Td>
                        <Td className="text-right font-semibold">{fmtINR(it.qty * it.rate)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <Label>Overall Discount %</Label>
                  <div className="flex gap-2">
                    <Input type="number" value={reviseDiscountPct} onChange={(e: any) => setReviseDiscountPct(Number(e.target.value) || 0)} data-testid="quot-rev-discount-pct" />
                    <Button variant="outline" onClick={applyPctToAllRates} data-testid="quot-rev-apply-pct">Apply</Button>
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>Note (optional)</Label>
                  <Input value={reviseNote} onChange={(e: any) => setReviseNote(e.target.value)} placeholder="e.g. Revised after customer negotiation" data-testid="quot-rev-note" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 border-t border-slate-200 dark:border-slate-700">
                <div className="text-sm"><span className="text-slate-500">Sub Total:</span> <b>{fmtINR(t.sub)}</b></div>
                <div className="text-sm"><span className="text-slate-500">GST:</span> <b>{fmtINR(t.gst)}</b></div>
                <div className="text-sm text-emerald-700"><span className="text-slate-500">New Total:</span> <b>{fmtINR(t.total)}</b>
                  {prevTotal !== t.total && <span className={"ml-2 text-xs " + (t.total < prevTotal ? "text-emerald-600" : "text-rose-600")}>{t.total < prevTotal ? "▼" : "▲"} {fmtINR(Math.abs(t.total - prevTotal))}</span>}
                </div>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReviseQ(null)} data-testid="quot-rev-cancel">Cancel</Button>
                <Button onClick={saveRevision} data-testid="quot-rev-save">Save Revision</Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Close Quotation Modal */}
      <Modal open={!!closeQ} onClose={() => setCloseQ(null)} title={closeQ ? `Close Quotation — ${closeQ.number}` : ""} size="md">
        {closeQ && (
          <div className="space-y-3" data-testid="quot-close-modal">
            <div className="text-xs text-slate-500">Provide a reason for closing this quotation. This will lock further edits (you can reopen it later from Actions).</div>
            <div>
              <Label>Reason *</Label>
              <Select value={closeReason} onChange={(e: any) => setCloseReason(e.target.value)} data-testid="quot-close-reason">
                {CLOSE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </Select>
            </div>
            <div>
              <Label>Additional note (optional)</Label>
              <Textarea rows={3} value={closeNote} onChange={(e: any) => setCloseNote(e.target.value)} data-testid="quot-close-note" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setCloseQ(null)} data-testid="quot-close-cancel">Cancel</Button>
              <Button onClick={saveClose} data-testid="quot-close-save">Close Quotation</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revision History Modal */}
      <Modal open={!!historyQ} onClose={() => setHistoryQ(null)} title={historyQ ? `Revision History — ${historyQ.number}` : ""} size="lg">
        {historyQ && (() => {
          const orig = calcDocTotals(historyQ.items); // NOTE: items on the record now match the latest revision
          const revs = (historyQ.revisions || []).slice().sort((a, b) => a.revNo - b.revNo);
          return (
            <div className="space-y-3" data-testid="quot-history-modal">
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
                <Table>
                  <thead>
                    <tr>
                      <Th>Rev</Th>
                      <Th>Date</Th>
                      <Th>Revised By</Th>
                      <Th className="text-right">Discount %</Th>
                      <Th className="text-right">Sub Total</Th>
                      <Th className="text-right">Total</Th>
                      <Th>Note</Th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-slate-50 dark:bg-slate-800/40">
                      <Td className="font-mono font-semibold">R0</Td>
                      <Td>{historyQ.date}</Td>
                      <Td>{db.users.find(u => u.id === historyQ.ownerId)?.name || "—"}</Td>
                      <Td className="text-right text-slate-400">—</Td>
                      <Td className="text-right">—</Td>
                      <Td className="text-right font-semibold">— (Original)</Td>
                      <Td className="text-xs text-slate-500">Original quotation</Td>
                    </tr>
                    {revs.length === 0 ? (
                      <tr><Td colSpan={7}><Empty title="No revisions yet" subtitle="Use the Revise button to create one." /></Td></tr>
                    ) : revs.map(r => (
                      <tr key={r.revNo}>
                        <Td className="font-mono font-semibold text-amber-700">R{r.revNo}</Td>
                        <Td>{r.date}</Td>
                        <Td>{r.revisedByName}</Td>
                        <Td className="text-right">{r.discountPct ? r.discountPct + "%" : "—"}</Td>
                        <Td className="text-right">{fmtINR(r.subTotal)}</Td>
                        <Td className="text-right font-semibold">{fmtINR(r.total)}</Td>
                        <Td className="text-xs text-slate-500">{r.note || "—"}</Td>
                      </tr>
                    ))}
                    {revs.length > 0 && (
                      <tr className="bg-emerald-50 dark:bg-emerald-900/20">
                        <Td className="font-mono font-semibold text-emerald-700">Current</Td>
                        <Td>—</Td>
                        <Td>—</Td>
                        <Td className="text-right text-slate-400">—</Td>
                        <Td className="text-right">{fmtINR(orig.sub)}</Td>
                        <Td className="text-right font-semibold text-emerald-700">{fmtINR(orig.total)}</Td>
                        <Td className="text-xs text-slate-500">Latest saved</Td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <div className="flex justify-end pt-1">
                <Button variant="ghost" onClick={() => setHistoryQ(null)} data-testid="quot-history-close">Close</Button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
